import { useEffect, useState, useRef } from 'react';
import { Paperclip, X, Send } from 'lucide-react';
import { api, buildQuery } from '../../lib/api';
import type { Assignment, AssignmentAttachment } from '../../types';
import { formatDateTime } from '../../lib/format';
import { useUpload } from '../../lib/useUpload';
import { Spinner, EmptyState, StatusBadge, Alert, UploadProgress, inputClass, primaryBtn, secondaryBtn } from '../../components/ui';

function AttachmentsList({ label, attachments }: { label: string; attachments: AssignmentAttachment[] }) {
  if (!attachments.length) return null;
  return (
    <div className="mt-3">
      <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
      <div className="flex flex-wrap gap-2">
        {attachments.map((att) => (
          <a
            key={att.id}
            href={att.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <Paperclip className="h-3 w-3" />
            {att.filename}
          </a>
        ))}
      </div>
    </div>
  );
}

function ProgressSlider({
  value,
  onCommit,
}: {
  value: number;
  onCommit: (v: number) => void;
}) {
  const [local, setLocal] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => setLocal(value), [value]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const handleChange = (v: number) => {
    setLocal(v);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onCommit(v), 500);
  };

  return (
    <input
      type="range"
      min={0}
      max={100}
      value={local}
      onChange={(e) => handleChange(Number(e.target.value))}
      className="flex-1 max-w-xs"
    />
  );
}

export default function Assignments() {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [respondingId, setRespondingId] = useState<number | null>(null);
  const [responseText, setResponseText] = useState('');
  const [responseFiles, setResponseFiles] = useState<File[]>([]);
  const respondUpload = useUpload();
  const [responseError, setResponseError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = () => {
    setLoading(true);
    api
      .get<Assignment[]>(`/assignments${buildQuery({ status: statusFilter })}`)
      .then(setAssignments)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, [statusFilter]);

  const updateStatus = async (id: number, status: string, progress?: number) => {
    try {
      await api.patch(`/assignments/${id}/status`, { status, progress });
      load();
    } catch {
      // ignore
    }
  };

  const handleSubmitResponse = async (id: number) => {
    if (!responseText.trim() && responseFiles.length === 0) return;
    setResponseError('');
    respondUpload.reset();
    try {
      const formData = new FormData();
      if (responseText.trim()) formData.append('response_text', responseText.trim());
      for (const file of responseFiles) {
        formData.append('response_attachments', file);
      }
      await respondUpload.run(`/assignments/${id}/respond`, formData);
      setRespondingId(null);
      setResponseText('');
      setResponseFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      load();
    } catch (err) {
      // Keep the chosen files attached and show why, so a failed upload can be
      // retried without re-picking everything.
      setResponseError((err as Error).message);
    }
  };

  const removeResponseFile = (idx: number) => {
    setResponseFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">My Assignments</h1>

      <div className="mb-4">
        <select
          className="w-48 px-4 py-2 border border-gray-300 rounded-lg text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {loading ? (
        <Spinner label="Loading assignments..." />
      ) : assignments.length === 0 ? (
        <EmptyState message="No assignments found." />
      ) : (
        <div className="grid gap-4">
          {assignments.map((a) => (
            <div key={a.id} className="bg-white p-5 rounded-lg shadow-lg">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
                <h3 className="font-semibold">{a.title}</h3>
                <StatusBadge status={a.status} />
              </div>
              {a.description && <p className="text-sm text-gray-500 mb-2">{a.description}</p>}
              <div className="flex flex-wrap gap-4 text-xs text-gray-500 mb-4">
                <span>Assigned by: <strong>{a.assigned_by_name}</strong></span>
                <span>Due: {formatDateTime(a.due_date)}</span>
              </div>

              <AttachmentsList label="Attachments from assignor" attachments={a.assignor_attachments} />

              {a.response_text && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs font-medium text-blue-600 mb-1">Your Response</p>
                  <p className="text-sm text-gray-700">{a.response_text}</p>
                </div>
              )}

              <AttachmentsList label="Response attachments" attachments={a.response_attachments} />

              <div className="max-w-md mb-4 mt-3">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Progress</span>
                  <span>{a.progress}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${a.progress}%` }} />
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <ProgressSlider
                  value={a.progress}
                  onCommit={(v) => updateStatus(a.id, a.status, v)}
                />
                <select
                  className={inputClass}
                  style={{ width: 'auto' }}
                  value={a.status}
                  onChange={(e) => updateStatus(a.id, e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
                <button
                  onClick={() => updateStatus(a.id, 'completed', 100)}
                  disabled={a.status === 'completed'}
                  className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Mark Done
                </button>
              </div>

              {respondingId === a.id ? (
                <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
                  <p className="text-sm font-medium text-gray-700">Submit Response</p>
                  <textarea
                    className={inputClass}
                    rows={3}
                    placeholder="Describe your response..."
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                  />
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        setResponseFiles((prev) => [...prev, ...files].slice(0, 15));
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                    >
                      <Paperclip className="h-3 w-3" /> Attach files (max 15)
                    </button>
                  </div>
                  {responseFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {responseFiles.map((file, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-xs">
                          <Paperclip className="h-3 w-3" />
                          {file.name}
                          <button onClick={() => removeResponseFile(idx)} className="text-red-400 hover:text-red-600">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <UploadProgress
                    percent={respondUpload.percent}
                    phase={respondUpload.phase}
                    error={respondUpload.error}
                    onCancel={respondUpload.cancel}
                  />
                  {responseError && <Alert>{responseError}</Alert>}
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setRespondingId(null);
                        setResponseText('');
                        setResponseFiles([]);
                        setResponseError('');
                      }}
                      className={secondaryBtn}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleSubmitResponse(a.id)}
                      disabled={respondUpload.busy || (!responseText.trim() && responseFiles.length === 0)}
                      className={primaryBtn}
                    >
                      <Send className="h-4 w-4" />
                      {respondUpload.phase === 'uploading'
                        ? `Uploading ${respondUpload.percent}%`
                        : respondUpload.phase === 'processing' ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setRespondingId(a.id);
                    setResponseText(a.response_text || '');
                    setResponseFiles([]);
                  }}
                  className="mt-4 px-4 py-2 border border-primary text-primary rounded-lg text-sm font-medium hover:bg-primary/5 inline-flex items-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  {a.response_text ? 'Edit Response' : 'Respond'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
